import EntryLanguageFooter from "../../components/EntryLanguageFooter";
import React, { useMemo, useState } from "react";
import useLanguage from "../../hooks/useLanguage";
import "./termsEntry.scss";
import {
  getCommunicationConfig,
  updateCommunicationValues,
  captureCommunication,
  normalizeConsentPhone,
} from "./communicationConsent";

const text = (node) => node?.textContent?.replace(/\s+/g, " ").trim() || "";
// Labels can contain several links, nested emphasis and punctuation. Keep
// their reading order as plain serialized metadata, never DOM nodes in drafts.
function labelParts(node) {
  return Array.from(node?.childNodes || []).flatMap((child) => {
    if (child.nodeType === 3)
      return [{ text: child.textContent.replace(/\s+/g, " ") }];
    if (child.nodeName === "A")
      return [{ text: text(child), href: child.href }];
    return labelParts(child);
  });
}

export function normalizeTermsDocument(html) {
  const root = document.createElement("div");
  root.innerHTML = html;
  // The server owns legal content, the form owns its presentation. Retain
  // words, structure, emphasis and links; do not persist the normalized copy.
  root.querySelectorAll("*").forEach((node) => {
    ["style", "class", "align", "width", "height"].forEach((name) =>
      node.removeAttribute(name),
    );
    if (node.tagName === "A") {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  return root.innerHTML;
}

export function parseTermsForm(html) {
  const root = document.createElement("div");
  root.innerHTML = html;
  const form = root.querySelector("form");
  if (!form) return null;
  const fields = Array.from(form.querySelectorAll(".form-group")).map(
    (group) => {
      const input = group.querySelector("input, select, textarea");
      const label = group.querySelector("label");
      const link = label?.querySelector("a");
      if (!input?.name) throw new Error("Terms field has no name");
      return {
        name: input.name,
        type:
          input.tagName === "INPUT" ? input.type : input.tagName.toLowerCase(),
        label: text(label),
        labelParts: labelParts(label),
        required: input.required,
        placeholder: input.placeholder || "",
        small: text(group.querySelector("small")),
        link: link ? { href: link.href, text: text(link) } : null,
        options:
          input.tagName === "SELECT"
            ? Array.from(input.options).map((option) => ({
                value: option.value,
                label: text(option),
              }))
            : undefined,
      };
    },
  );
  if (
    !fields.length ||
    new Set(fields.map((field) => field.name)).size !== fields.length
  )
    throw new Error("Invalid terms fields");
  return fields;
}

export default function TermsAcceptanceForm({
  definition,
  fields,
  nameseo,
  radioid,
  radioName,
  onAccept,
}) {
  const communication = useMemo(
    () => getCommunicationConfig(definition, fields),
    [definition, fields],
  );
  const [values, setValues] = useState({});
  const [ticked, setTicked] = useState(false);
  const { mayorLanguage } = useLanguage();
  const spanish = mayorLanguage !== "en";
  const documentHtml = useMemo(
    () => {
      if (!fields) return normalizeTermsDocument(definition.text);
      if (!communication) return null;
      const root = document.createElement("div");
      root.innerHTML = definition.text;
      root.querySelectorAll("form").forEach(form => form.remove());
      return normalizeTermsDocument(root.innerHTML);
    },
    [fields, definition.text, communication],
  );
  const phoneVisible = communication && (
    values[communication.channels.whatsapp_sms] || values[communication.channels.llamadas]
  );
  const phone = phoneVisible ? normalizeConsentPhone(values[communication.phoneField]) : "";
  const phoneInvalid = phone === null;
  const visibleFields = fields?.filter(field =>
    !communication || field.name !== communication.phoneField || phoneVisible,
  );
  const valid = !phoneInvalid && (fields
    ? fields.every(
        (field) =>
          !field.required ||
          (field.type === "checkbox"
            ? values[field.name] === true
            : String(values[field.name] || "").trim() !== ""),
      )
    : ticked);
  const submit = (event) => {
    event.preventDefault();
    if (!valid) return;
    const selected = fields
      ? fields
          .filter((field) => field.type === "checkbox" && values[field.name])
          .map((field) => field.name)
      : [];
    onAccept({
      extra: Array.from(new Set(["terms", ...selected])),
      formMetadata: fields || [],
      formValues: values,
      isCustomForm: !!fields,
      text: definition.text,
      legalDocument: definition.legalDocument,
      ...(communication ? {
        communicationConsent: captureCommunication(values, communication),
        customerPhone: phone || "",
      } : {}),
    });
  };
  const copy = spanish
    ? {
        title: "Términos y condiciones",
        eyebrow: "Antes de continuar",
        intro: "Revisa la información y completa los campos requeridos.",
        accept: "He leído y acepto los términos y condiciones",
        submit: "Aceptar y continuar",
        optional: "Opcional",
        required: "Los campos con * son obligatorios.",
      }
    : {
        title: "Terms and conditions",
        eyebrow: "Before you continue",
        intro: "Review the information and complete the required fields.",
        accept: "I have read and accept the terms and conditions",
        submit: "Accept and continue",
        optional: "Optional",
        required: "Fields marked * are required.",
      };
  const renderLabel = (field) =>
    field.labelParts?.length ? (
      field.labelParts.map((part, index) =>
        part.href ? (
          <a key={index} href={part.href} target="_blank" rel="noreferrer">
            {part.text}
          </a>
        ) : (
          <React.Fragment key={index}>{part.text}</React.Fragment>
        ),
      )
    ) : (
      <>
        {field.link
          ? field.label.replace(field.link.text, "").trim()
          : field.label}
        {field.link && (
          <>
            {" "}
            <a href={field.link.href} target="_blank" rel="noreferrer">
              {field.link.text}
            </a>
          </>
        )}
      </>
    );
  return (
    <section
      className={`terms-entry ${nameseo === "johnnie-music" || String(radioid) === "2025" ? "terms-entry-johnnie-music" : ""}`}
      aria-label={copy.title}
    >
      <header className="terms-entry__header">
        <p className="terms-entry__eyebrow">{copy.eyebrow}</p>
        <h1 className="terms-entry__title">{copy.title}</h1>
        {radioName && <p className="terms-entry__radio">{radioName}</p>}
        {fields && <p className="terms-entry__intro">{copy.intro}</p>}
      </header>
      <form onSubmit={submit}>
        <div className="terms-container">
          {fields ? (
            <>
              {documentHtml && (
                <div className="terms-document" dangerouslySetInnerHTML={{ __html: documentHtml }} />
              )}
              {fields.some((field) => field.required) && (
                <p className="terms-entry__legend">{copy.required}</p>
              )}
              <div className="terms-fields">
                {visibleFields.map((field) => {
                  const id = `entry-terms-${encodeURIComponent(field.name)}`;
                  const isCheckbox = field.type === "checkbox";
                  const isChannel = communication && Object.values(communication.channels).includes(field.name);
                  const helperId = field.small ? `${id}-help` : undefined;
                  const control = {
                    id,
                    name: field.name,
                    ...(communication && field.name === communication.phoneField ? {
                      autoComplete: "tel", maxLength: 24,
                      "aria-invalid": phoneInvalid,
                      title: spanish ? "Incluye el indicativo del país y entre 7 y 15 dígitos." : "Include the country code and 7 to 15 digits.",
                    } : {}),
                    required: field.required,
                    placeholder: field.placeholder,
                    "aria-describedby": helperId,
                    className: "terms-field__control",
                    onChange: (event) => {
                      const value = isCheckbox
                        ? event.target.checked
                        : event.target.value;
                      setValues((old) => updateCommunicationValues(old, field.name, value, communication));
                    },
                  };
                  const label = (
                    <label className="terms-field__label" htmlFor={id}>
                      {renderLabel(field)}
                      {field.required && (
                        <span
                          className="terms-field__required"
                          aria-hidden="true"
                        />
                      )}
                      {!field.required &&
                        !/opcional|optional/i.test(field.small || "") && (
                          <span className="terms-field__optional">
                            {copy.optional}
                          </span>
                        )}
                    </label>
                  );
                  const helper = field.small && (
                    <small className="terms-field__help" id={helperId}>
                      {field.small}
                    </small>
                  );
                  return (
                    <div
                      className={`terms-field ${isChannel ? "terms-field--channel" : ""} ${isCheckbox ? "terms-field--checkbox" : "terms-field--input"} ${field.type === "textarea" ? "terms-field--wide" : ""}`}
                      key={field.name}
                    >
                      {isCheckbox ? (
                        <>
                          <input
                            {...control}
                            type="checkbox"
                            checked={!!values[field.name]}
                          />
                          <div className="terms-field__copy">
                            {label}
                            {helper}
                          </div>
                        </>
                      ) : (
                        <>
                          {label}
                          {field.type === "select" ? (
                            <select
                              {...control}
                              value={values[field.name] || ""}
                            >
                              <option value="" />
                              {field.options.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : field.type === "textarea" ? (
                            <textarea
                              {...control}
                              rows={3}
                              value={values[field.name] || ""}
                            />
                          ) : (
                            <input
                              {...control}
                              type={field.type}
                              value={values[field.name] || ""}
                            />
                          )}
                          {helper}
                          {communication && field.name === communication.phoneField && phoneInvalid && (
                            <small className="terms-field__help" role="alert">{spanish ? "Revisa el teléfono: incluye entre 7 y 15 dígitos o deja el campo vacío." : "Check the phone: use 7 to 15 digits or leave the field empty."}</small>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div
                className="terms-document"
                dangerouslySetInnerHTML={{ __html: documentHtml }}
              />
              <div className="terms-field terms-field--checkbox terms-entry__acceptance">
                <input
                  className="terms-field__control"
                  id="entry-terms-acceptance"
                  type="checkbox"
                  checked={ticked}
                  onChange={(event) => setTicked(event.target.checked)}
                />
                <label
                  className="terms-field__label"
                  htmlFor="entry-terms-acceptance"
                >
                  {copy.accept}
                </label>
              </div>
            </>
          )}
        </div>
        <div className="terms-entry__actions">
          <button
            type="submit"
            className="terms-entry__submit"
            disabled={!valid}
          >
            {copy.submit}
            <span aria-hidden="true"> →</span>
          </button>
        </div>
      </form>
      <EntryLanguageFooter />
    </section>
  );
}
